/*
 * /fishing-rules — интерфейс: место (карта, «Где я», список регионов) → бассейн →
 * что действует в выбранный день, меры и нормы, все пункты с поиском, проверка
 * улова по фото. Логика без интерфейса — engine.js (её гоняет node --test).
 *
 * Тексты — window.__FR.T (из lang-файла), адреса данных — window.__FR.cfg.urls
 * (со штампом версии из AssetRouter::data: файл правил назовёт другой файл, и
 * вчерашняя копия из кэша не должна встретиться с сегодняшним кодом).
 */
import {
    periodState, itemsFor, activeOn, upcoming,
    search, sizesAndBags, catchVerdict, norm,
} from './engine.js';
import { initCatchCheck } from './catch.js';
import { createRegions, myRegion, rememberPlace, rememberRegion } from '../regions/index.js';

const FR = window.__FR || { T: {}, cfg: {} };
const T = FR.T || {};
const CFG = FR.cfg || {};
const URLS = CFG.urls || {};
const SLUG = 'fishing-rules';

const $ = (id) => document.getElementById(id);
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const tr = (k, r) => {
    let s = T[k] != null ? String(T[k]) : k;
    if (r) for (const n of Object.keys(r)) s = s.split(':' + n).join(String(r[n]));
    return s;
};
const ae = (ev, data) => { try { window.__ae && window.__ae(ev, Object.assign({ slug: SLUG }, data || {})); } catch (e) { /* telemetry never breaks the page */ } };
const stage = (n) => { try { window.__stage && window.__stage(n); } catch (e) { /* telemetry never breaks the page */ } };

const state = {
    index: null, basins: {},
    lat: null, lon: null, code: '', name: '', exact: true, km: 0, abroad: '', via: '', auto: false,
    basin: '', date: todayIso(), tab: 'now', completed: false,
};
/* Где точка — общий примитив регионов (docs/regions.md): свою копию контуров тул не держит. */
const RG = createRegions(Object.assign({ locale: 'ru' }, CFG.regions || {}));
let mine = { last: null, profile: null };

function todayIso() {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

async function loadJSON(url) {
    const r = await fetch(url, { cache: 'force-cache' });
    if (!r.ok) throw new Error('HTTP ' + r.status + ' ' + url);
    return r.json();
}

let indexP = null;
/* Отказ не запоминается: следующий вызов спросит сеть снова, а не вернёт
   тот же отвергнутый промис до перезагрузки страницы. */
function ensureIndex() {
    if (!indexP) indexP = loadJSON(URLS.index).then((j) => (state.index = j), (e) => { indexP = null; throw e; });
    return indexP;
}

function failLoad(e, where) {
    try { window.__handleError && window.__handleError(e, where, { say: T.load_error }); } catch (x) { /* no handler */ }
    $('frNow').innerHTML = '<div class="tc-panel"><p class="tc-hint">' + esc(T.load_error) + '</p></div>';
}
function ensureBasin(key) {
    if (state.basins[key]) return Promise.resolve(state.basins[key]);
    const url = (URLS.basins || {})[key];
    if (!url) return Promise.reject(new Error('no basin ' + key));
    return loadJSON(url).then((j) => (state.basins[key] = j));
}

function basinsFor(code) {
    const list = (state.index && state.index.basins) || [];
    return list.filter((b) => (b.subjects || []).includes(code));
}
function basinMeta(key) {
    return ((state.index && state.index.basins) || []).find((b) => b.key === key) || null;
}

/* ── Даты по-русски ──────────────────────────────────────────────────────── */

const MONTHS = String(T.months_gen || '').split(',');
function fmtMd(s) {
    const m = /^(\d{2})-(\d{2})$/.exec(s || '');
    if (!m) return null;
    return (+m[2]) + '\u00a0' + (MONTHS[+m[1] - 1] || m[1]);
}
function fmtIso(s) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
    if (!m) return s || '';
    return (+m[3]) + '\u00a0' + (MONTHS[+m[2] - 1] || m[2]) + '\u00a0' + m[1];
}
function fmtPeriod(p) {
    const edge = (v, key) => (v === 'thaw' || v === 'freeze') ? T[key + '_' + v]
        : tr(key + '_day', { d: fmtMd(v) || fmtIso(v) });
    if (p.from === '01-01' && p.to === '12-31') return T.year_round;
    return edge(p.from, 'from') + ' ' + edge(p.to, 'to');
}

/* ── Место ───────────────────────────────────────────────────────────────── */

let map = null, marker = null, outline = null;

function writeUrl() {
    try {
        const u = new URL(location.href);
        ['lat', 'lon', 'region', 'date', 'basin'].forEach((k) => u.searchParams.delete(k));
        if (state.lat != null) { u.searchParams.set('lat', state.lat.toFixed(4)); u.searchParams.set('lon', state.lon.toFixed(4)); }
        else if (state.code) u.searchParams.set('region', state.code);
        if (state.basin && basinsFor(state.code).length > 1) u.searchParams.set('basin', state.basin);
        if (state.date !== todayIso()) u.searchParams.set('date', state.date);
        history.replaceState(history.state, '', u.toString());
    } catch (e) { /* a URL we cannot write is not worth an error */ }
}

async function selectPoint(lat, lon, source) {
    let w;
    try { w = await RG.at(lat, lon, { a3: 'RUS' }); } catch (e) { failLoad(e, 'fishing_rules_regions'); return; }
    state.lat = lat; state.lon = lon; state.via = '';
    if (source !== 'url') rememberPlace({ lat, lon });
    if (map && window.L) {
        if (!marker) marker = window.L.marker([lat, lon], { keyboard: false }).addTo(map);
        else marker.setLatLng([lat, lon]);
    }
    if (!w.region) {
        // Точка за границей — называем страну и предлагаем «мой регион», а не молчаливый список.
        state.code = ''; state.name = ''; state.abroad = w.country ? w.country.name : '';
        $('frRegion').value = '';
        drawOutline(null);
        renderPlaceNote();
        renderMine();
        ae('feature_use', { action: 'place', source: source || 'map', outside: w.country ? w.country.code : 'sea' });
        renderAll();
        writeUrl();
        return;
    }
    state.abroad = '';
    state.exact = w.region.exact; state.km = w.region.km;
    ae('feature_use', { action: 'place', source: source || 'map', exact: w.region.exact ? 1 : 0 });
    await selectRegion(w.region.code, source, true);
}

async function selectRegion(code, source, fromPoint) {
    let r;
    try { [r] = await Promise.all([RG.region('RUS', code), ensureIndex()]); } catch (e) { failLoad(e, 'fishing_rules_index'); return; }
    if (!r || !basinsFor(r.code).length) return;
    if (!fromPoint) { state.lat = null; state.lon = null; state.exact = true; state.km = 0; state.abroad = ''; if (marker && map) { map.removeLayer(marker); marker = null; } }
    state.code = r.code; state.name = r.name;
    // auto-* — открыто само (память, профиль): говорится вслух и не считается результатом.
    state.auto = /^auto-/.test(source || '');
    const via = String(source || '').replace(/^auto-/, '');
    state.via = !fromPoint && (via === 'profile' || via === 'last') ? via : '';
    if (source !== 'url' && !state.auto) rememberRegion({ code: r.code, a3: 'RUS', name: r.name });
    $('frRegion').value = r.code;
    drawOutline(r, !fromPoint);
    const list = basinsFor(r.code);
    if (!list.some((b) => b.key === state.basin)) state.basin = list.length ? list[0].key : '';
    renderBasinPicker(list);
    renderPlaceNote();
    renderMine();
    stage('place');
    writeUrl();
    await renderAll();
}

function renderPlaceNote() {
    const el = $('frPlaceNote');
    if (state.abroad) { el.textContent = tr('place_abroad', { country: state.abroad }); return; }
    if (!state.code) { el.textContent = state.lat != null ? T.place_outside : ''; return; }
    if (state.lat != null && !state.exact) el.textContent = tr('place_near', { name: state.name, km: state.km });
    else if (state.lat != null) el.textContent = tr('place_in', { name: state.name });
    else if (state.via === 'profile') el.textContent = tr('place_from_profile', { name: state.name, city: (mine.profile && mine.profile.place) || '' });
    else if (state.via === 'last') el.textContent = tr('place_from_last', { name: state.name });
    else el.textContent = '';
}

/* «Мой регион» — кнопки к региону города из профиля и к прошлому выбору; та, что
   совпадает с открытым регионом, не показывается. */
function mineButtons(primary) {
    const out = [];
    const seen = new Set([state.code]);
    for (const [cand, key, src] of [[mine.profile, 'mine_profile', 'profile'], [mine.last, 'mine_last', 'last']]) {
        if (!cand || seen.has(cand.code) || !basinsFor(cand.code).length) continue;
        seen.add(cand.code);
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'tc-btn' + (primary && !out.length ? '' : ' ghost');
        b.textContent = tr(key, { name: cand.name });
        b.addEventListener('click', () => {
            ae('feature_use', { action: 'mine', source: src });
            selectRegion(cand.code, src);
        });
        out.push(b);
    }
    return out;
}
/* Под полем — когда регион открыт (переключиться на свой). Точка вне России: путь вперёд
   стоит в главной панели (renderNoPlace); в одну колонку (телефон) главная панель уходит
   под всю форму, и те же кнопки повторяются здесь — fr-mine-away, в две колонки скрыты. */
function renderMine() {
    const box = $('frMine');
    if (!box) return;
    box.textContent = '';
    const away = !state.code && state.lat != null;
    box.classList.toggle('fr-mine-away', away);
    if (state.code || away) mineButtons(away).forEach((b) => box.appendChild(b));
    box.hidden = !box.childElementCount;
}
/* Главная панель без региона: что с точкой — крупно, и сразу выход — свой регион или список. */
function renderNoPlace(now) {
    const head = state.abroad ? tr('abroad_head', { country: state.abroad }) : state.lat != null ? T.sea_head : '';
    now.innerHTML = '<div class="tc-panel fr-noplace">'
        + (head ? '<div class="tc-answer"><div class="tc-answer-v fr-noplace-v">' + esc(head) + '</div>'
            + '<p class="tc-answer-sub">' + esc(T.noplace_sub) + '</p></div>'
            : '<p class="tc-hint">' + esc(T.pick_place) + '</p>')
        + '<div class="tc-actions fr-noplace-go"></div></div>';
    const go = now.querySelector('.fr-noplace-go');
    mineButtons(true).forEach((b) => go.appendChild(b));
    const list = document.createElement('button');
    list.type = 'button';
    list.className = 'tc-btn ghost';
    list.textContent = T.pick_from_list;
    list.addEventListener('click', () => {
        const sel = $('frRegion');
        sel.scrollIntoView({ block: 'nearest' });
        sel.focus();
        try { if (sel.showPicker) sel.showPicker(); } catch (e) { /* not every browser opens a select by script */ }
    });
    go.appendChild(list);
}

function renderBasinPicker(list) {
    const wrap = $('frBasinWrap'), box = $('frBasins');
    box.textContent = '';
    if (list.length <= 1) { wrap.hidden = true; return; }
    wrap.hidden = false;
    for (const b of list) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tc-seg-b' + (b.key === state.basin ? ' on' : '');
        btn.setAttribute('aria-pressed', b.key === state.basin ? 'true' : 'false');
        btn.textContent = b.short;
        btn.addEventListener('click', () => {
            if (state.basin === b.key) return;
            state.basin = b.key;
            renderBasinPicker(list);
            writeUrl();
            ae('feature_use', { action: 'basin', basin: b.key });
            renderAll();
        });
        box.appendChild(btn);
    }
    const note = document.createElement('p');
    note.className = 'tc-note';
    note.textContent = T.basin_many;
    box.appendChild(note);
}

function drawOutline(r, fit) {
    if (!map || !window.L) return;
    if (outline) { map.removeLayer(outline); outline = null; }
    if (!r) return;
    const latlngs = r.polys.map((poly) => poly.map((ring) => ring.map((p) => [p[1], p[0]])));
    outline = window.L.polygon(latlngs, { color: '#2fb3a6', weight: 2, fill: true, fillOpacity: 0.06, interactive: false }).addTo(map);
    if (fit) {
        try {
            const b = outline.getBounds();
            if (r.bb[2] - r.bb[0] > 300) map.setView([(r.bb[1] + r.bb[3]) / 2, 175], 4);   // Чукотка через 180°
            else map.fitBounds(b, { padding: [12, 12], maxZoom: 9 });
        } catch (e) { /* bounds of a degenerate outline */ }
    }
}

function initMap() {
    const box = $('frMap');
    if (!window.L) { box.hidden = true; $('frMapNote').hidden = false; return; }
    map = window.L.map(box, { zoomControl: true, attributionControl: true, worldCopyJump: true, minZoom: 2 }).setView([60, 70], 3);
    if (window.__mapTiles) window.__mapTiles.base(window.L, map, null, { slug: SLUG, style: 'auto', control: false, opts: { maxZoom: 18 } });
    else if (window.__basemapFallback) window.__basemapFallback(window.L, map, '');
    map.on('click', (e) => selectPoint(e.latlng.lat, e.latlng.lng, 'map'));
}

function waitFor(test, ms) {
    return new Promise((res) => {
        if (test()) return res(true);
        const t0 = Date.now();
        const iv = setInterval(() => {
            if (test()) { clearInterval(iv); res(true); } else if (Date.now() - t0 > ms) { clearInterval(iv); res(false); }
        }, 100);
    });
}

function locateMe() {
    const btn = $('frLocate');
    if (!navigator.geolocation) { toast(T.gps_none, 'error'); return; }
    btn.disabled = true;
    btn.textContent = T.gps_wait;
    navigator.geolocation.getCurrentPosition((pos) => {
        btn.disabled = false; btn.textContent = T.btn_locate;
        const { latitude, longitude } = pos.coords;
        if (map) map.setView([latitude, longitude], 10);
        selectPoint(latitude, longitude, 'gps');
    }, (err) => {
        btn.disabled = false; btn.textContent = T.btn_locate;
        toast(err && err.code === 1 ? T.gps_denied : T.gps_fail, 'error');
        ae('feature_use', { action: 'gps_fail', code: err && err.code });
    }, { enableHighAccuracy: false, timeout: 15000, maximumAge: 120000 });
}

function toast(msg, type) {
    try { if (window.__toast) { window.__toast(msg, type || 'info'); return; } } catch (e) { /* fall through */ }
    $('frPlaceNote').textContent = msg;
}

/* ── Отрисовка ───────────────────────────────────────────────────────────── */

function currentItems() {
    const b = state.basins[state.basin];
    return b ? itemsFor(b, state.code) : [];
}

async function renderAll() {
    const now = $('frNow');
    if (!state.code) {
        renderNoPlace(now);
        $('frSizes').textContent = '';
        $('frAll').textContent = '';
        renderSource(null);
        return;
    }
    if (!state.basin) {
        now.innerHTML = '<div class="tc-panel"><p class="tc-hint">' + esc(tr('no_basin', { name: state.name })) + '</p></div>';
        renderSource(null);
        return;
    }
    if (!state.basins[state.basin]) {
        now.innerHTML = '<div class="tc-panel"><p class="tc-hint">' + esc(T.loading) + '</p></div>';
        try { await ensureBasin(state.basin); } catch (e) {
            failLoad(e, 'fishing_rules_basin_load');
            return;
        }
    }
    renderSource(basinMeta(state.basin));
    renderNow();
    renderSizes();
    renderList();
    if (!state.completed && !state.auto) {
        state.completed = true;
        try { window.__aeToolComplete && window.__aeToolComplete({ basin: state.basin, exact: state.exact ? 1 : 0 }); } catch (e) { /* telemetry never breaks the page */ }
    }
}

function quoteBlock(it) {
    const meta = basinMeta(state.basin);
    return '<details class="fr-q"><summary>' + esc(tr('clause_text', { n: it.clause })) + '</summary>'
        + '<blockquote>' + esc(it.q) + '</blockquote>'
        + (it.act ? '<p class="fr-q-src">' + esc(it.act) + '</p>'
            : meta ? '<p class="fr-q-src">' + esc(meta.act) + (meta.edition ? ', ' + esc(meta.edition) : '') + '</p>' : '')
        + '</details>';
}

function cardHtml(it, st, extra) {
    const per = (it.periods || []).map(fmtPeriod).join('; ');
    const cls = st === 'yes' ? 'on' : st === 'maybe' ? 'maybe' : '';
    return '<article class="fr-card ' + cls + '">'
        + '<div class="fr-card-h"><span class="fr-card-what">' + esc(it.what || T.what_all) + '</span>'
        + (st === 'maybe' ? '<span class="fr-badge maybe">' + esc(T.badge_ice) + '</span>' : '')
        + (extra ? '<span class="fr-badge">' + esc(extra) + '</span>' : '')
        + '</div>'
        + (per ? '<div class="fr-card-when">' + esc(per) + '</div>' : '')
        + (it.where ? '<div class="fr-card-where">' + esc(it.where) + '</div>' : '')
        + quoteBlock(it)
        + '</article>';
}

function renderNow() {
    const items = currentItems();
    const on = activeOn(items, state.date);
    const soon = upcoming(items, state.date, 30);
    const date = fmtIso(state.date);
    const parts = [];
    parts.push('<div class="fr-now-h"><b>' + esc(state.name) + '</b> · ' + esc((basinMeta(state.basin) || {}).short || '') + ' · ' + esc(date) + '</div>');
    if (on.length) {
        parts.push('<p class="fr-sum on">' + esc(tr('now_count', { n: on.length, date })) + '</p>');
        parts.push(on.map((x) => cardHtml(x.item, x.state)).join(''));
    } else {
        parts.push('<p class="fr-sum">' + esc(tr('now_none', { date })) + '</p>');
    }
    const allowed = items.filter((it) => it.k === 'allowed');
    if (on.length && allowed.length) {
        parts.push('<h3 class="fr-sub">' + esc(T.allowed_title) + '</h3>');
        parts.push(allowed.map((it) => cardHtml(it, 'no')).join(''));
    }
    if (soon.length) {
        parts.push('<h3 class="fr-sub">' + esc(T.soon_title) + '</h3>');
        parts.push(soon.slice(0, 8).map((x) => cardHtml(x.item, 'no', tr('soon_days', { n: x.days }))).join(''));
    }
    const always = items.filter((it) => it.k === 'species' || it.k === 'area' || it.k === 'gear');
    if (always.length) {
        parts.push('<p class="tc-note">' + esc(tr('always_note', { n: always.length })) + '</p>');
    }
    $('frNow').innerHTML = parts.join('');
}

function renderSizes() {
    const rows = sizesAndBags(currentItems(), state.code);
    const f = norm($('frSizeFilter').value || '');
    const shown = f ? rows.filter((r) => norm(r.sp + ' ' + r.where).includes(f)) : rows;
    const box = $('frSizes');
    if (!rows.length) { box.innerHTML = '<p class="tc-hint">' + esc(T.sizes_none) + '</p>'; return; }
    let h = '<table class="fr-table"><thead><tr><th>' + esc(T.col_species) + '</th><th>' + esc(T.col_size) + '</th><th>' + esc(T.col_bag) + '</th><th>' + esc(T.col_clause) + '</th></tr></thead><tbody>';
    for (const r of shown) {
        h += '<tr><td>' + esc(r.sp) + (r.where ? '<div class="fr-note">' + esc(r.where) + '</div>' : '') + (r.notes.length ? '<div class="fr-note">' + esc(r.notes.join('; ')) + '</div>' : '') + '</td>'
            + '<td>' + (r.cm != null ? esc(tr('cm', { n: String(r.cm).replace('.', ',') })) : '—') + '</td>'
            + '<td>' + (r.bag != null ? esc(r.bag + ' ' + (r.unit || '')) : '—') + '</td>'
            + '<td class="fr-refs">' + r.refs.map((id) => '<button type="button" class="fr-ref" data-ref="' + esc(id) + '">' + esc(clauseOf(id)) + '</button>').join(' ') + '</td></tr>';
    }
    h += '</tbody></table>';
    if (f && !shown.length) h += '<p class="tc-hint">' + esc(T.sizes_nomatch) + '</p>';
    box.innerHTML = h;
    fillCatchSpecies(rows);
}

function clauseOf(id) {
    const b = state.basins[state.basin];
    const it = b && (b.items || []).find((x) => x.id === id);
    return it ? tr('clause_short', { n: it.clause }) : id;
}

function renderList() {
    const b = state.basins[state.basin];
    if (!b) return;
    const q = $('frSearch').value.trim();
    const wide = $('frSearchBasin').checked;
    let items = wide ? (b.items || []) : itemsFor(b, state.code);
    if (q) items = search(items, q);
    const box = $('frAll');
    if (!items.length) { box.innerHTML = '<p class="tc-hint">' + esc(q ? T.search_none : T.list_none) + '</p>'; return; }
    const groups = new Map();
    for (const it of items) {
        const h = it.head || T.head_general;
        if (!groups.has(h)) groups.set(h, []);
        groups.get(h).push(it);
    }
    let html = q ? '<p class="fr-sum">' + esc(tr('search_found', { n: items.length })) + '</p>' : '';
    for (const [h, list] of groups) {
        html += '<section class="fr-group"><h3 class="fr-sub">' + esc(h) + '</h3>';
        for (const it of list) {
            const per = (it.periods || []).map(fmtPeriod).join('; ');
            html += '<div class="fr-item" id="fr-' + esc(it.id) + '"><div class="fr-item-n">' + esc(tr('clause_short', { n: it.clause })) + (per ? ' · ' + esc(per) : '') + '</div><p>' + highlight(it.q, q) + '</p></div>';
        }
        html += '</section>';
    }
    box.innerHTML = html;
}

function highlight(text, q) {
    const s = esc(text);
    if (!q) return s;
    const words = norm(q).split(/\s+/).filter((w) => w.length >= 3).map((w) => w.slice(0, Math.max(2, w.length - 2)));
    if (!words.length) return s;
    const re = new RegExp('(' + words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/е/g, '[её]')).join('|') + ')', 'gi');
    return s.replace(re, '<mark>$1</mark>');
}

function renderSource(meta) {
    const box = $('frSource');
    if (!meta) { box.hidden = true; return; }
    box.hidden = false;
    const checked = state.index && state.index.checked ? fmtIso(state.index.checked) : '';
    box.innerHTML = '<div class="tc-eyebrow">' + esc(T.source_title) + '</div>'
        + '<p class="fr-src-act">' + esc(meta.act) + (meta.edition ? ' (' + esc(meta.edition) + ')' : '') + '</p>'
        + '<p class="tc-note">' + esc(tr('source_checked', { date: checked })) + '</p>'
        + (meta.coverage ? '<p class="tc-note">' + esc(meta.coverage) + '</p>' : '')
        + (meta.extra || []).map((x) => '<p class="tc-note">' + esc(tr('source_extra', { act: x.act, what: x.title })) + (x.src ? ' <a href="' + esc(x.src) + '" target="_blank" rel="noopener">' + esc(T.src_text) + '</a>' : '') + '</p>').join('')
        + '<p class="fr-src-links">'
        + (meta.official ? '<a href="' + esc(meta.official) + '" target="_blank" rel="noopener">' + esc(T.src_official) + '</a>' : '')
        + (meta.src ? ' <a href="' + esc(meta.src) + '" target="_blank" rel="noopener">' + esc(T.src_text) + '</a>' : '')
        + '</p>';
}

/* ── Вкладки ─────────────────────────────────────────────────────────────── */

function setTab(tab) {
    state.tab = tab;
    document.querySelectorAll('.fr-tab').forEach((b) => {
        const on = b.dataset.tab === tab;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    document.querySelectorAll('.fr-pane').forEach((p) => { p.hidden = p.dataset.pane !== tab; });
    ae('feature_use', { action: 'tab', tab });
}

/* ── Улов ────────────────────────────────────────────────────────────────── */

let catchApi = null;
function fillCatchSpecies(rows) {
    if (catchApi) catchApi.setSpecies(rows.filter((r) => r.cm != null));
}

/* ── Старт ───────────────────────────────────────────────────────────────── */

async function boot() {
    $('frDate').value = state.date;
    document.querySelectorAll('.fr-tab').forEach((b) => b.addEventListener('click', () => setTab(b.dataset.tab)));
    $('frDate').addEventListener('change', () => {
        const v = $('frDate').value;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return;
        state.date = v;
        writeUrl();
        ae('feature_use', { action: 'date' });
        renderAll();
    });
    $('frToday').addEventListener('click', () => { state.date = todayIso(); $('frDate').value = state.date; writeUrl(); renderAll(); });
    $('frLocate').addEventListener('click', locateMe);
    $('frRegion').addEventListener('change', () => { const v = $('frRegion').value; if (v) selectRegion(v, 'list'); });
    $('frSizeFilter').addEventListener('input', renderSizes);
    let st = null;
    $('frSearch').addEventListener('input', () => { clearTimeout(st); st = setTimeout(() => { renderList(); if ($('frSearch').value.trim()) ae('feature_use', { action: 'search' }); }, 200); });
    $('frSearchBasin').addEventListener('change', renderList);
    document.addEventListener('click', (e) => {
        const b = e.target.closest && e.target.closest('.fr-ref');
        if (!b) return;
        setTab('all');
        $('frSearch').value = '';
        $('frSearchBasin').checked = false;
        renderList();
        const el = document.getElementById('fr-' + b.dataset.ref);
        if (el) { el.classList.add('hl'); el.scrollIntoView({ block: 'nearest' }); setTimeout(() => el.classList.remove('hl'), 2500); }
    });

    catchApi = initCatchCheck({ T, tr, ae, slug: SLUG, verdict: catchVerdict });

    /* Нажатие «Где я» или вкладки до того, как проснулся модуль, держит
       __earlyAction из <head>; здесь оно исполняется, а не теряется. */
    try {
        window.__earlyAction && window.__earlyAction.claim(SLUG, (a) => {
            if (a === 'locate') locateMe();
            else if (String(a).startsWith('tab:')) setTab(String(a).slice(4));
        });
    } catch (e) { /* no shim — the buttons are ours anyway */ }

    let regions;
    try {
        [regions] = await Promise.all([RG.list('RUS'), ensureIndex()]);
    } catch (e) {
        failLoad(e, 'fishing_rules_boot');
        return;
    }
    const sel = $('frRegion');
    for (const r of regions) {
        if (!basinsFor(r.code).length) continue;
        const o = document.createElement('option');
        o.value = r.code; o.textContent = r.name;
        sel.appendChild(o);
    }
    mine = await myRegion(RG, 'RUS');
    const u = new URL(location.href);
    const d = u.searchParams.get('date');
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) { state.date = d; $('frDate').value = d; }
    const wantBasin = u.searchParams.get('basin');
    if (wantBasin) state.basin = wantBasin;
    const lat = parseFloat(u.searchParams.get('lat')), lon = parseFloat(u.searchParams.get('lon'));
    const reg = u.searchParams.get('region');
    // The place from the link is answered before the map is up: the rules do not
    // wait for tiles. The map then shows the same place.
    // Without a place in the link — the person's own region: the last one chosen on the
    // site, else the profile city's. Said aloud in the place note, never silent.
    if (isFinite(lat) && isFinite(lon)) await selectPoint(lat, lon, 'url');
    else if (reg) await selectRegion(reg, 'url');
    else if (mine.last) await selectRegion(mine.last.code, 'auto-last');
    else if (mine.profile) await selectRegion(mine.profile.code, 'auto-profile');
    renderMine();

    const ok = await waitFor(() => !!window.L, 8000);
    if (!ok) { $('frMap').hidden = true; $('frMapNote').hidden = false; return; }
    initMap();
    if (state.lat != null) {
        map.setView([state.lat, state.lon], 9);
        marker = window.L.marker([state.lat, state.lon], { keyboard: false }).addTo(map);
    }
    if (state.code) {
        const r = await RG.region('RUS', state.code).catch(() => null);
        if (r) drawOutline(r, state.lat == null);
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
